/**
 * Full-screen centered loading overlay — matches shadcn/ui dialog overlay style.
 *
 * Two usage modes:
 *
 * 1. **Declarative (React):**
 *    ```tsx
 *    <LoadingOverlay visible={isLoading} />
 *    ```
 *
 * 2. **Imperative (from hooks/callbacks):**
 *    ```ts
 *    const dismiss = showLoadingOverlay();
 *    await someAsyncWork();
 *    dismiss();
 *    ```
 */
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { createRoot, type Root } from 'react-dom/client';

// Shared overlay markup — matches shadcn DialogOverlay (bg-black/80, z-50)
const overlayClassName =
  'fixed inset-0 z-50 flex items-center justify-center bg-black/80 animate-in fade-in-0';
//   ^^^^^ same z-index & bg as shadcn DialogOverlay

function OverlayUI() {
  return (
    <div data-loading-overlay="" className={overlayClassName}>
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Declarative component
// ---------------------------------------------------------------------------

interface LoadingOverlayProps {
  visible: boolean;
}

export function LoadingOverlay({ visible }: LoadingOverlayProps) {
  if (!visible) return null;
  return createPortal(<OverlayUI />, document.body);
}

// ---------------------------------------------------------------------------
// Imperative API
// ---------------------------------------------------------------------------

let activeRoot: Root | null = null;
let activeContainer: HTMLDivElement | null = null;

/**
 * Show a centered loading overlay. Returns a dismiss function.
 *
 * Safe to call multiple times — only one overlay is shown at a time.
 *
 * @param delay  ms to wait before actually showing the overlay (default 0 = immediate).
 *               Use 200-300ms for operations that usually complete quickly — prevents a
 *               jarring flash on fast networks while still covering slow ones.
 */
export function showLoadingOverlay(delay = 0): () => void {
  // Reuse existing overlay if already showing
  if (activeRoot && activeContainer) {
    return () => dismissLoadingOverlay();
  }

  // No delay — show immediately (existing behavior)
  if (delay <= 0) {
    mountOverlay();
    return () => dismissLoadingOverlay();
  }

  // Delayed — only show if the operation takes longer than `delay` ms
  let dismissed = false;
  const timer = setTimeout(() => {
    if (!dismissed) mountOverlay();
  }, delay);

  return () => {
    dismissed = true;
    clearTimeout(timer);
    dismissLoadingOverlay();
  };
}

function mountOverlay() {
  if (activeRoot && activeContainer) return; // already showing

  const container = document.createElement('div');
  document.body.appendChild(container);

  const root = createRoot(container);
  root.render(<OverlayUI />);

  activeRoot = root;
  activeContainer = container;
}

export function dismissLoadingOverlay() {
  if (activeRoot) {
    activeRoot.unmount();
    activeRoot = null;
  }
  if (activeContainer) {
    activeContainer.remove();
    activeContainer = null;
  }
}
